import { useEffect } from "react";
import { AlertTriangle, BookOpenCheck, CalendarCheck2, Gauge, Users } from "lucide-react";
import { toast } from "sonner";
import { useDashboard } from "@/hooks/useDashboard";
import { errorMessage } from "@/lib/api";
import { nf } from "@/lib/format";
import { L } from "@/i18n";
import { KpiCard, Stat } from "@/components/dashboard/KpiCard";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { DashboardSkeleton } from "@/components/dashboard/DashboardSkeleton";
import { ModuleChart } from "@/components/dashboard/ModuleChart";
import { UserStatusChart } from "@/components/dashboard/UserStatusChart";
import { SubscriptionCard } from "@/components/dashboard/SubscriptionCard";
import { ExamsCard } from "@/components/dashboard/ExamsCard";
import { RecentUsersTable } from "@/components/dashboard/RecentUsersTable";
import { ModuleBreakdown } from "@/components/dashboard/ModuleBreakdown";

const KpiGrid = ({ d }) => {
  const rate = d.sessions.total ? Math.round((d.sessions.completed / d.sessions.total) * 100) : 0;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
      <KpiCard icon={Users} label={L("Users", "Utilizadores")} value={nf.format(d.users.total)} testId="kpi-card-users">
        <Stat color="#10b981" label={L("Appr.", "Aprov.")} value={d.users.approved} testId="kpi-users-approved" />
        <Stat color="#f59e0b" label="Pend." value={d.users.pending} testId="kpi-users-pending" />
        <Stat color="#f43f5e" label={L("Block.", "Bloq.")} value={d.users.blocked} testId="kpi-users-blocked" />
      </KpiCard>
      <KpiCard icon={BookOpenCheck} label={L("Questions", "Perguntas")} value={nf.format(d.questions.total)} testId="kpi-card-questions" delay={60}>
        {L("{m} modules · {s} submodules", "{m} módulos · {s} submódulos", { m: d.questions.modules.length, s: d.questions.modules.reduce((s, m) => s + m.submodules, 0) })}
      </KpiCard>
      <KpiCard icon={Gauge} label={L("Quiz sessions", "Sessões de quiz")} value={nf.format(d.sessions.total)} testId="kpi-card-quizzes" delay={120}>
        {L("{c} completed · {r}% completion rate", "{c} concluídas · {r}% taxa de conclusão", { c: nf.format(d.sessions.completed), r: rate })}
      </KpiCard>
      <KpiCard icon={CalendarCheck2} label={L("Exams", "Exames")} value={nf.format(d.exams.total)} testId="kpi-card-exams" delay={180}>
        {L("{u} scheduled · {p} completed/expired", "{u} agendados · {p} realizados/expirados", { u: d.exams.upcoming.length, p: d.exams.pastCount })}
      </KpiCard>
    </div>
  );
};

const ErrorState = ({ error, onRetry }) => (
  <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-8 text-center" data-testid="dashboard-error">
    <AlertTriangle className="mx-auto h-6 w-6 text-rose-400" />
    <p className="mt-3 text-sm text-slate-200">{L("Could not load data.", "Não foi possível carregar os dados.")}</p>
    <p className="mt-1 text-xs text-slate-400">{errorMessage(error)}</p>
    <button onClick={onRetry} className="mt-4 rounded-md bg-amber-500 px-4 py-2 text-xs font-semibold text-[#0a1425]" data-testid="dashboard-retry-button">
      {L("Try again", "Tentar novamente")}
    </button>
  </div>
);

export default function Dashboard() {
  const { data, error, isLoading, isFetching, refetch, dataUpdatedAt } = useDashboard();

  useEffect(() => {
    if (error) toast.error(L("Failed to load dashboard: {e}", "Erro ao carregar dashboard: {e}", { e: errorMessage(error) }));
  }, [error]);

  const refresh = async () => {
    const r = await refetch();
    if (!r.error) toast.success(L("Data refreshed.", "Dados atualizados."));
  };

  return (
    <div data-testid="dashboard-page">
      <DashboardHeader updatedAt={dataUpdatedAt || null} fetching={isFetching} onRefresh={refresh} />
      {isLoading && <DashboardSkeleton />}
      {!data && error && <ErrorState error={error} onRetry={refetch} />}
      {data && (
        <>
          <KpiGrid d={data} />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
            <ModuleChart modules={data.questions.modules} total={data.questions.total} />
            <UserStatusChart users={data.users} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
            <RecentUsersTable users={data.recentUsers} />
            <div className="lg:col-span-4 grid gap-6">
              <SubscriptionCard plans={data.plans} available={data.hasPlanData} total={data.users.total} />
              <ExamsCard exams={data.exams} />
            </div>
          </div>
          <ModuleBreakdown modules={data.questions.modules} />
        </>
      )}
    </div>
  );
}
